import { defineStore } from 'pinia'
import datasetApi from '@/api/dataset'
import { useUserStore } from '@/stores/user'
import type { DatasetAttributes } from 'common'
import { notStarted, isLoaded, loading, loaded, type Loading, error } from '@/utils/loading'
import { sanitizeDataset } from '@/utils/dataset'

export const useDatasetsStore = defineStore('datasets', {
  state: () => ({
    datasets: notStarted as Loading<DatasetAttributes[]>,
    currentDataset: notStarted as Loading<DatasetAttributes>
  }),
  actions: {
    clearCurrentDataset() {
      this.currentDataset = notStarted
    },
    async setCurrentDataset(
      datasetId: number,
      options?: { logVisit?: boolean }
    ): Promise<Loading<DatasetAttributes>> {
      this.currentDataset = loading
      return await datasetApi
        .get(datasetId, options)
        .then((dataset) => {
          if (options?.logVisit) {
            useUserStore().recordReportVisitOffline({
              report_type: 'dataset',
              report_id: dataset.id,
              report_name: dataset.name
            })
          }
          const sanitizedDataset = {
            ...dataset,
            query: sanitizeDataset(dataset.query)
          }
          this.currentDataset = loaded(sanitizedDataset)
          return this.currentDataset
        })
        .catch((err) => {
          this.currentDataset = error(err)
          return this.currentDataset
        })
    },
    async create(
      body: Omit<
        DatasetAttributes,
        | 'id'
        | 'created_on'
        | 'modified_on'
        | 'deleted_on'
        | 'user_id'
        | 'next_execution'
        | 'last_execution'
      >
    ) {
      return datasetApi.create(body)
    },
    async refreshList(): Promise<void> {
      this.datasets = loading
      const result = await datasetApi.list()
      const sanitizedResult = result.map((dataset) => ({
        ...dataset,
        query: sanitizeDataset(dataset.query)
      }))
      this.datasets = loaded(sanitizedResult)
    },
    async update(
      id: number,
      body: Omit<
        DatasetAttributes,
        | 'id'
        | 'created_on'
        | 'modified_on'
        | 'deleted_on'
        | 'next_execution'
        | 'user_id'
        | 'last_execution'
      >
    ) {
      const data = await datasetApi.update(id, body)

      if (isLoaded(this.datasets)) {
        const existingRecordIndex = this.datasets.data.findIndex((_) => _.id === id)
        if (existingRecordIndex > -1 && data) {
          this.datasets.data = [data]
            .concat(this.datasets.data.slice(0, existingRecordIndex))
            .concat(this.datasets.data.slice(existingRecordIndex + 1))
        }
      }
      return data
    },
    async delete(id: number) {
      return datasetApi.deleteDataset(id).then((response) => {
        if (isLoaded(this.datasets)) {
          this.datasets.data = this.datasets.data.filter((_) => _.id !== id)
        }
        useUserStore().removeReportVisit('dataset', id)
        return response
      })
    }
  }
})
